<template>
  <DataTable
      scrollable
      scroll-height="flex"
      :value="assetsIndexPageStore.getAssets"
      lazy
      paginator
      :first="assetsIndexPageStore.getFirstPage"
      :rows="assetsIndexPageStore.params.per_page"
      :rowsPerPageOptions="assetsIndexPageStore.pages"
      :total-records="assetsIndexPageStore.totalRecords"
      :loading="assetsIndexPageStore.loading"
      @page="assetsIndexPageStore.onPage($event)"
      @sort="assetsIndexPageStore.onSort($event)"
      @filter="assetsIndexPageStore.onFilter($event)"
      @update:rows="assetsIndexPageStore.onRowsUpdate"
      data-key="id"
      size="small"
      removable-sort
      always-show-paginator
      v-model:filters="assetsIndexPageStore.filters"
      filterDisplay="menu"
  >
    <template #header>
      <Toolbar class="border-none shadow-none -m-3.5 rounded-none">
        <template #start>
          <Button data-cy="asset-create-btn" v-if="$auth.hasPermission('create:assets')" :label="$t('common.create')" class="mr-2"
                  @click="createAsset">
            <template #icon="slotProps">
              <IconPlus class="w-4 h-4 mr-1" :class="slotProps.class"/>
            </template>
          </Button>
          <div class="flex-1">
            <IconField icon-position="left">
              <InputIcon>
                <IconSearch class="text-surface-400 dark:text-surface-600 h-4 w-4"/>
              </InputIcon>
              <InputText
                  :placeholder="$t('pages.assets.index.search_assets')"
                  class="sm:py-2"
                  v-model="assetsIndexPageStore.params.filter.search"
                  @keyup="assetsIndexPageStore.onSearch"
              />
            </IconField>
          </div>
        </template>

        <template #end>
          <div class="flex items-stretch justify-end space-x-2">
            <div class="flex-1 divide-x divide-slate-300 grid grid-cols-3">
              <IndexHeaderStat
                  :title="$t('pages.assets.index.assets_total')"
                  :value="assetsDashboardPageStore?.getAssetsTotalCount"
                  :loading="assetsDashboardPageStore?.getAssetsTotalCountLoading"
              />
              <IndexHeaderStat
                  :title="$t('pages.assets.index.without_devices')"
                  :value="assetsDashboardPageStore?.getAssetsWithoutDevicesCount"
                  :loading="assetsDashboardPageStore?.getAssetsWithoutDevicesCountLoading"
              />
              <IndexHeaderStat
                  :title="$t('pages.assets.index.needing_an_update')"
                  :value="assetsDashboardPageStore?.getAssetsNeedingAnUpdateCount"
                  :loading="assetsDashboardPageStore?.getAssetsNeedingAnUpdateCountLoading"
              />
            </div>
            <Button text severity="secondary" @click="refreshStats(true)">
              <template #icon="{class: iconClass}">
                <IconRefresh :class="iconClass" class="w-5 h-5"/>
              </template>
            </Button>
          </div>
        </template>
      </Toolbar>
    </template>
    <Column field="id" :header="$t('common.id')" sortable class="pl-6 lg:pl-8 w-20 uppercase"/>
    <Column field="name" :header="$t('common.name')" sortable class="w-64 whitespace-nowrap">
      <template #body="{data, index}">
        <div class="flex items-center justify-start space-x-3">
          <div class="h-9 w-9 bg-primary-50 rounded-md inline-flex items-center justify-center">
            <IconTruck class="text-primary-900 opacity-50"/>
          </div>
          <router-link :to="{name: 'assets.show', params: { asset: data.id }}">
            <Button
                :label="data.name"
                link
                size="small"
                aria-haspopup="true"
            />
          </router-link>
        </div>
      </template>
    </Column>
    <Column
        field="asset_type.name"
        :header="$t('common.type')"
        filter-field="asset_type_ids"
        :show-filter-match-modes="false"
        class="w-32 whitespace-nowrap"
    >
      <template #body="{data}">
        {{ data.asset_type?.name || '-' }}
      </template>
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="assetTypesStore.getAssetTypeOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>
    <Column
        field="company.name"
        :header="$t('common.company')"
        filterField="company_ids"
        :showFilterMatchModes="false"
        class="w-56 whitespace-nowrap"
    >
      <template #body="{data}">
        {{ data.company?.name || '-' }}
      </template>
      <template #filter="{filterModel,filterCallback}">
        <MultiSelect
            v-model="filterModel.value"
            :options="companiesStore.getCompanyOptions"
            option-label="name"
            option-value="id"
            :placeholder="$t('common.any')"
            class="w-full"
            :max-selected-labels="2"
        />
      </template>
    </Column>
    <Column field="devices" :header="$t('common.devices')">
      <template #body="{data}">
        <div class="flex flex-row space-x-1.5">
          <Tag :severity="device.requires_update === 1 ? 'info' : 'secondary'" v-for="device in data?.devices"
               :value="device?.device_model?.name"
               class="whitespace-nowrap"
          >
            <template #icon="{ class: iconClass }">
              <IconAlertTriangleFilled v-if="device.requires_update === 1" :class="iconClass" class="h-4 w-4"/>
            </template>
          </Tag>
        </div>
      </template>
    </Column>
    <Column field="devices" :header="$t('pages.assets.index.rx_swins')">
      <template #body="{data}">
        <div class="flex flex-row space-x-1.5">
          <Tag :severity="'secondary'" v-for="rxSwin in data?.rx_swins"
               class="whitespace-nowrap"
          >
            <span>{{ rxSwin.regulation.name }}_{{ rxSwin.identifier }}</span>
          </Tag>
        </div>
      </template>
    </Column>
  </DataTable>

  <AssetSlideOver ref="slideOver" @saved="onSave"/>
</template>

<script>
import {useAssetsIndexPageStore} from "@/stores/pages/assetsIndexPage.js";
import {useAssetsDashboardPageStore} from "@/stores/pages/assetsDashboardPage.js";
import {useAssetTypesStore} from "@/stores/assetTypes.js";
import {useCompaniesStore} from "@/stores/companies.js";
import {mapStores} from "pinia";
import AssetSlideOver from "@/Slideovers/AssetSlideOver.vue";
import {IconAlertTriangleFilled, IconPlus, IconSearch, IconTruck, IconRefresh} from "@tabler/icons-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import AssetDashboardCard from "@/Widgets/Cards/AssetDashboardCard.vue";
import IndexHeaderStat from "@/Widgets/Cards/IndexHeaderStat.vue";

export default {
  name: "AssetsIndexView",
  computed: {
    ...mapStores(useAssetsIndexPageStore),
    ...mapStores(useAssetsDashboardPageStore),
    ...mapStores(useAssetTypesStore),
    ...mapStores(useCompaniesStore),
  },
  components: {
    IndexHeaderStat,
    AssetDashboardCard,
    IconTruck,
    IconPlus,
    IconSearch,
    IconAlertTriangleFilled,
    AssetSlideOver,
    IconField,
    InputIcon,
    IconRefresh,
  },
  methods: {
    editAsset(asset) {
      this.$refs.slideOver.editAsset(asset);
    },
    createAsset() {
      this.$refs.slideOver.createAsset();
    },
    onSave() {
      this.assetsIndexPageStore.loadData();
      this.refreshStats(true);
    },
    refreshStats(forceRefresh = false) {
      this.assetsDashboardPageStore.fetchAssetCount({}, forceRefresh);
      this.assetsDashboardPageStore.fetchAssetsWithoutDevicesCount({}, forceRefresh);
      this.assetsDashboardPageStore.fetchAssetsNeedingAnUpdate({}, forceRefresh);
    }
  },
  created() {
    this.assetsIndexPageStore.initFilters();
  },
  async mounted() {
    this.assetsIndexPageStore.loadData();
    await this.assetTypesStore.indexAssetTypeOptions();
    await this.companiesStore.indexCompanyOptions();

    this.refreshStats();
  }
}
</script>